import React from 'react';
import {
  Collapse,
  ExternalLink,
  Gutter,
  List,
  Modal,
  Paragraph,
} from '@common/components';
import { FontSize } from '@common/tokens';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export function RolePermissionModal({ visible, onClose }: Props) {
  return (
    <>
      <Modal
        title="Role Permissions"
        visible={visible}
        onCancel={onClose}
        footer={
          <div className="member-form-role-modal-footer">
            For more information, see the{' '}
            <ExternalLink url="https://help.workshealth.com/en/articles/6797319-works-membership-levels">
              Membership Levels article
            </ExternalLink>
          </div>
        }
      >
        <div className="role-permission-modal__panels">
          <Collapse>
            <Collapse.Panel title="Organization and MSP Admin" key="1">
              <Paragraph size="lg">
                Organization and MSP Admins are system administrators, they are
                super-users that have full admin rights. They can take all
                actions that other roles can perform. Organization Admins can
                view invoices (along with Billing Managers). They are the only
                ones who can:
              </Paragraph>
              <Gutter size="lg" direction="vertical">
                <List>
                  <List.Item>Set up and update bill rates</List.Item>
                  <List.Item>
                    Bypass approval when creating a Staffing Request or
                    extension
                  </List.Item>
                  <List.Item>
                    Approve / reject items on behalf of designated approvers
                  </List.Item>
                </List>
              </Gutter>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
            <Collapse.Panel title="Program Admin and Program Manager" key="2">
              <Paragraph size="lg">
                All Program Managers can shortlist submissions, and also take
                all actions available to Hiring Managers and Compliance
                Managers. Unlike Organization Admins, Program Managers cannot
                manage bill rates, bypass approvals, approve / reject items on
                behalf of approvers, or view invoices.
              </Paragraph>
              <Paragraph size="lg">
                There are two Program roles. Program Admin have access to some
                admin functionalities (set up templates, approval chains and
                memberships, and view rate cards), while Program Managers have
                no access to any admin functionalities.
              </Paragraph>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
            <Collapse.Panel title="Hiring Manager" key="3">
              <Paragraph size="lg">
                Hiring Managers are clinician managers overseeing a single unit
                or a group of units. They can create Staffing Requests, review
                shortlisted submissions, extend offers and extensions, view and
                create shift openings (if access granted), and verify time
                worked line item disputes.
              </Paragraph>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
            <Collapse.Panel title="Billing Manager" key="4">
              <Paragraph size="lg">
                Billing managers are responsible for financial operations. They
                can view invoices and verify line item disputes within the
                Billing section. They have view-only access to the Staffing and
                Team sections, and no access to other sections.
              </Paragraph>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
            <Collapse.Panel title="Compliance Manager" key="5">
              <Paragraph size="lg">
                Compliance managers are responsible for onboarding and ongoing
                compliance. They can set up compliance requirements, and verify
                compliance items. They have view-only access to the Staffing
                section and do not have access to the Candidate, Shifts, or
                Billing sections.
              </Paragraph>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
            <Collapse.Panel title="Executive and Read Only" key="6">
              <Paragraph size="lg">
                Executives and Read Only are users who need to sign onto Works
                to approve items or view reports, but do not use Works for other
                operational purposes. They have view-only access to the Staffing
                and Team sections, and no access to other sections. Executives,
                Read Only and all other roles can view reports if their account
                has been set up to access them.
              </Paragraph>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
            <Collapse.Panel title="Compliance Coordinator" key="7">
              <Paragraph size="lg">
                On-Demand Coordinators are support staff that perform tasks
                specific to shift management. They can manage on-demand staff
                and shift requests. They don’t have access to other sections.
              </Paragraph>
              <Paragraph size="lg">
                Note: Approver status and Reports are not role-dependent.
              </Paragraph>
            </Collapse.Panel>
          </Collapse>
        </div>
      </Modal>
      <style jsx>{`
        .role-permission-modal__panels {
          font-size: ${FontSize.md};
        }
        .member-form-role-modal-footer {
          padding: 14px 12px;
          font-size: ${FontSize.md};
          text-align: left;
        }
      `}</style>
    </>
  );
}
